import { useMutation } from "@apollo/client/react"
import { useEffect, useState } from "react"
import { toast } from "sonner"
import { gql } from "~/__generated__"
import type { RecordingSectionFragment } from "~/__generated__/graphql"
import { Button } from "~/ui/button"
import { GraphqlError } from "~/ui/graphql-error"

const CORRECT = gql(/* GraphQL */ `
  mutation CorrectTranscript($input: TranscriptCorrectInput!) {
    transcriptCorrect(input: $input) {
      section {
        ...RecordingSection
      }
    }
  }
`)

export function TranscriptEditor({
  section,
  editable,
  onDirtyChange,
  onSaved,
}: {
  section: RecordingSectionFragment
  editable: boolean
  onDirtyChange: (id: string, dirty: boolean) => void
  onSaved: () => Promise<void>
}) {
  const [edit, setEdit] = useState<{ text: string; original: string; version: number } | null>(null)
  const [correct, { loading, error, reset }] = useMutation(CORRECT)
  const dirty = !!edit && edit.text !== edit.original
  useEffect(() => {
    onDirtyChange(section.id, dirty || loading)
    return () => onDirtyChange(section.id, false)
  }, [section.id, dirty, loading, onDirtyChange])
  return (
    <div className="mt-4 space-y-3">
      <h5 className="text-sm font-semibold">
        Transcript
        {section.transcriptVersion > 0 ? ` · correction ${section.transcriptVersion}` : ""}
      </h5>
      {edit ? (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Fix recognition errors before writing the post. Your original video and timestamped
            transcript stay in history.
          </p>
          <label htmlFor={`transcript-${section.id}`} className="block text-sm font-medium">
            Transcript for section {section.position}
          </label>
          <textarea
            id={`transcript-${section.id}`}
            aria-label={`Transcript for section ${section.position}`}
            className="min-h-40 w-full rounded-lg border bg-background p-3 text-sm"
            value={edit.text}
            maxLength={50000}
            disabled={loading}
            onChange={(e) => setEdit({ ...edit, text: e.target.value })}
          />
          {edit.version !== section.transcriptVersion && (
            <p role="alert" className="text-sm text-destructive">
              A newer correction was saved elsewhere. Copy your edits, then cancel and reopen the
              editor to use the latest version.
            </p>
          )}
          {error && <GraphqlError error={error} />}
          <div className="flex gap-3">
            <Button
              type="button"
              size="sm"
              disabled={
                loading || !dirty || !edit.text.trim() || edit.version !== section.transcriptVersion
              }
              onClick={async () => {
                try {
                  const result = await correct({
                    variables: {
                      input: { id: section.id, text: edit.text, expectedVersion: edit.version },
                    },
                  })
                  if (!result.data) return
                  setEdit(null)
                  toast.success("Transcript correction saved")
                  try {
                    await onSaved()
                  } catch {
                    toast.error("Correction saved. Reload to refresh the post draft status.")
                  }
                } catch {
                  /* Displayed above. */
                }
              }}
            >
              {loading ? "Saving…" : "Save transcript correction"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={loading}
              onClick={() => {
                if (!dirty || window.confirm("Discard this unsaved transcript correction?")) {
                  setEdit(null)
                  reset()
                }
              }}
            >
              Cancel correction
            </Button>
          </div>
        </div>
      ) : (
        <>
          <p className="whitespace-pre-wrap text-sm leading-7">{section.transcript}</p>
          {editable && section.status === "ready" && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                reset()
                setEdit({
                  text: section.transcript,
                  original: section.transcript,
                  version: section.transcriptVersion,
                })
              }}
            >
              Correct section {section.position} transcript
            </Button>
          )}
        </>
      )}
      {(section.originalSegments.length > 0 || section.transcriptRevisions.length > 0) && (
        <details className="rounded-lg border p-3 text-sm">
          <summary className="cursor-pointer">Original transcript & correction history</summary>
          <p className="mt-3 text-muted-foreground">
            Timestamps refer to the original speech recognition. Corrections do not retime the video
            or replace drafts already generated. Write a new post draft to use the updated words.
          </p>
          {section.transcriptRevisions.map((revision) => (
            <div key={revision.id} className="mt-4">
              <h6 className="font-medium">
                {revision.number === 0 ? "Original recognition" : `Correction ${revision.number}`}
              </h6>
              <p className="mt-1 whitespace-pre-wrap">{revision.text}</p>
            </div>
          ))}
          {section.originalSegments.length > 0 && (
            <div className="mt-4 space-y-2">
              <h6 className="font-medium">Original timestamps (seconds)</h6>
              {section.originalSegments.map((segment, index) => (
                <p key={index}>
                  <span className="font-mono text-xs">
                    {segment.start.toFixed(1)}–{segment.end.toFixed(1)}
                  </span>{" "}
                  {segment.text}
                </p>
              ))}
            </div>
          )}
        </details>
      )}
    </div>
  )
}
